import { CheckCircle, XCircle, Loader, Send } from 'lucide-react';
import { getPlatformConfig } from '../../../constants/platformLogos';
import Button from '../../../components/ui/Button';

const PublishingProgressModal = ({ publishingStatus, onClose }) => {
  const allCompleted = publishingStatus?.every(s => s?.status === 'success' || s?.status === 'failed');
  const successCount = publishingStatus?.filter(s => s?.status === 'success')?.length;
  const failedCount = publishingStatus?.filter(s => s?.status === 'failed')?.length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'publishing':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Send className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'publishing':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'failed':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'publishing':
        return 'Publishing...';
      case 'success':
        return 'Published Successfully';
      case 'failed':
        return 'Publishing Failed';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {allCompleted ? 'Publishing Complete' : 'Publishing Responses'}
            </h3>
          </div>
          
          {allCompleted && (
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-700">
                  <strong>{successCount}</strong> successful
                </span>
              </div>
              {failedCount > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-gray-700">
                    <strong>{failedCount}</strong> failed
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {publishingStatus?.map((item) => {
              const platformConfig = getPlatformConfig(item?.platform);
              
              return (
                <div 
                  key={item?.id}
                  className={`border-2 rounded-lg p-4 transition-all ${getStatusColor(item?.status)}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(item?.status)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {platformConfig && (
                          <img 
                            src={platformConfig?.logo} 
                            alt={platformConfig?.alt}
                            className="w-4 h-4 object-contain"
                          />
                        )}
                        <span className="font-medium text-gray-900">
                          {platformConfig?.name || item?.platform}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 mb-2">
                        {getStatusText(item?.status)}
                      </p>

                      {/* Progress Bar */}
                      {item?.status === 'publishing' && (
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${item?.progress}%` }}
                          ></div>
                        </div>
                      )}

                      {/* Error Message */}
                      {item?.status === 'failed' && item?.error && (
                        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-800">
                          <strong>Error:</strong> {item?.error}
                        </div>
                      )}

                      {/* Success Details */}
                      {item?.status === 'success' && (
                        <div className="flex items-center gap-2 text-xs text-green-700">
                          <CheckCircle className="w-3 h-3" />
                          <span>Response published to {platformConfig?.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {allCompleted && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {failedCount > 0 ? (
                  <span className="text-orange-600">
                    ⚠️ Some responses failed to publish. Please try again.
                  </span>
                ) : (
                  <span className="text-green-600">
                    ✓ All responses published successfully!
                  </span>
                )}
              </div>
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishingProgressModal;